import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { collection, getDocs } from "firebase/firestore";

import Sidebar from "../../DesignSystem/Sidebar";
import SecondaryButton from "../../DesignSystem/Buttons/SecondaryButton";

import { auth, db } from "../../../backend/firebaseConfig";
import { useUserPermissions } from "../../SignIn/UserPermissionsContext";
import { useAdviserListWithUnappointed } from "../../../context/Adviser/Utils/UnappointedAppointedAdviserEmailList";

import { useParaplanningCases } from "../hooks/useParaplanningCases";
import { useParaplanningCharges } from "../hooks/useParaplanningCharges";
import {
  exportParaplanningCasesToExcel,
  exportAllParaplanningMonthsToExcel,
} from "../utils/exportParaplanningCasesToExcel";

import {
  getParaplannerNameByEmail,
  getParaplannerTypeByEmail,
} from "../utils/paraplannerGroups";

import {
  buildAdviserOptions,
  buildAssignedToOptions,
  buildCaseTypeOptions,
} from "../utils/addCaseModalUtils";
import { getParaplanningPermissionState } from "../utils/userPermissions";

import { PARAPLANNING_ASSIGNED_TO_OPTIONS } from "../constants/paraplanningAssignedToOptions";
import Tabs from "../../DesignSystem/Tabs/Tabs";
import ParaplanningSummaryCards from "./ParaplanningSummaryCards";
import ParaplanningFilters from "./ParaplanningFilters";
import ParaplanningFiltersDrawer from "./ParaplanningFiltersDrawer";
import ParaplanningSelectedFilterChips from "./ParaplanningSelectedFilterChips";
import ParaplanningCasesTable from "./ParaplanningCasesTable";
import AddCaseModal from "./AddCaseModal";

import "../../../css/Praplanning/ParaplanningDashboard.css";

import Breadcrumb from "../../DesignSystem/Breadcrumb/Breadcrumb";
import { ReactComponent as FilterIcon } from "../../../assets/icons/paraplanningDashboard/Filter(Filled).svg";
import { ReactComponent as Close } from "../../../assets/icons/close.svg";

import ParaplanningExcelImport from "../components/ParaplanningExcelImport";
import { useParaplannerSalaries } from "../hooks/useParaplannerSalaries";
import {
  formatParaplanningCurrency,
  formatParaplanningPercent,
  getAnnualSalaryForPeriod,
  getChargeVsSalaryPercentage,
  getParaplanningBonusAmount,
  getSalaryForSummaryMode,
  hasBonusBlockingGrade,
} from "../utils/paraplanningBonusUtils";

const hasAssignedTo = (item = {}) => {
  const assignedEmail = normaliseEmail(
    item?.assignedTo?.email ||
      item?.assignedToEmail ||
      item?.assignedTo?.value ||
      "",
  );

  const assignedName = String(
    item?.assignedTo?.name ||
      item?.assignedTo?.label ||
      item?.assignedToName ||
      "",
  ).trim();

  return Boolean(assignedEmail || assignedName);
};

const EMPTY_DRAWER_FILTERS = {
  catalogIds: [],
  adviserIds: [],
  caseTypeIds: [],
  statuses: [],
  assignedToIds: [],
};

const CATALOG_LABELS = {
  financial_planning: "Financial Planning",
  mortgage_protection: "Mortgage & Protection",
};

const STATUS_LABELS = {
  in_progress: "In Progress",
  completed: "Completed",
  cancelled: "Cancelled",
  npw: "NPW",
};

const getMonthKeyFromYearMonth = (year, monthIndex) => {
  const month = String(monthIndex + 1).padStart(2, "0");
  return `${year}-${month}`;
};

const getQuarterKey = (year, quarter) => {
  return `${year}-Q${quarter}`;
};

const getCurrentMonthKey = () => {
  const today = new Date();
  return getMonthKeyFromYearMonth(today.getFullYear(), today.getMonth());
};

const getCurrentQuarterKey = () => {
  const today = new Date();
  const quarter = Math.floor(today.getMonth() / 3) + 1;
  return getQuarterKey(today.getFullYear(), quarter);
};

const getMonthLabel = (monthKey, format = "long") => {
  if (!monthKey) return "";

  const [year, month] = monthKey.split("-");
  const date = new Date(Number(year), Number(month) - 1, 1);

  return date.toLocaleDateString("en-GB", {
    month: format,
    year: "numeric",
  });
};

const getQuarterLabel = (quarterKey) => {
  if (!quarterKey) return "";

  const [year, quarterValue] = quarterKey.split("-Q");
  const quarter = Number(quarterValue);

  const startMonth = (quarter - 1) * 3;
  const endMonth = startMonth + 2;

  const start = new Date(Number(year), startMonth, 1);
  const end = new Date(Number(year), endMonth, 1);

  const startLabel = start.toLocaleDateString("en-GB", { month: "short" });
  const endLabel = end.toLocaleDateString("en-GB", { month: "short" });

  return `Q${quarter} (${startLabel} - ${endLabel} ${year})`;
};

const getPreviousMonthKey = (monthKey) => {
  const [year, month] = monthKey.split("-");
  const date = new Date(Number(year), Number(month) - 2, 1);

  return getMonthKeyFromYearMonth(date.getFullYear(), date.getMonth());
};

const getPreviousQuarterKey = (quarterKey) => {
  const [year, quarterValue] = quarterKey.split("-Q");
  const quarter = Number(quarterValue);

  if (quarter > 1) {
    return `${year}-Q${quarter - 1}`;
  }

  return `${Number(year) - 1}-Q4`;
};

const getMonthOptionsForYear = () => {
  const today = new Date();
  const year = today.getFullYear();

  return Array.from({ length: 12 }, (_, index) => {
    const monthKey = getMonthKeyFromYearMonth(year, index);
    const label = getMonthLabel(monthKey);

    return {
      id: monthKey,
      value: monthKey,
      label,
      name: label,
    };
  });
};

const getQuarterOptionsForYear = () => {
  const today = new Date();
  const year = today.getFullYear();

  return Array.from({ length: 4 }, (_, index) => {
    const quarter = index + 1;
    const quarterKey = getQuarterKey(year, quarter);
    const label = getQuarterLabel(quarterKey);

    return {
      id: quarterKey,
      value: quarterKey,
      label,
      name: label,
    };
  });
};

const getExcelMonthOptionsFromJan = () => {
  const today = new Date();
  const year = today.getFullYear();
  const currentMonthIndex = today.getMonth();

  return Array.from({ length: currentMonthIndex + 1 }, (_, index) => {
    const monthKey = getMonthKeyFromYearMonth(year, index);

    return {
      id: monthKey,
      monthKey,
      label: getMonthLabel(monthKey, "short"),
    };
  });
};

const toDate = (value) => {
  if (!value) return null;

  if (value?.toDate) {
    return value.toDate();
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
};

const getCompletedDate = (item) => {
  if (item.status !== "completed") return null;
  return toDate(item.completionDate);
};

const getStartedDate = (item) => {
  return toDate(item.startDate || item.createdAt);
};

const hasCompletedOrNpwStatus = (item = {}) => {
  return item.status === "completed" || item.status === "npw";
};

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const getTurnaroundDays = (item = {}) => {
  if (!hasCompletedOrNpwStatus(item)) return null;

  const startDate = getStartedDate(item);
  const endDate = toDate(item.completionDate);

  if (!startDate || !endDate) return null;

  const diff = endDate.getTime() - startDate.getTime();
  if (diff < 0) return null;

  return diff / DAY_IN_MS;
};

const getAverageTurnaroundDays = (cases = []) => {
  const values = cases
    .map((item) => getTurnaroundDays(item))
    .filter((value) => typeof value === "number");

  if (!values.length) return null;

  const total = values.reduce((sum, value) => sum + value, 0);
  return Number((total / values.length).toFixed(1));
};

const formatTurnaroundDays = (days) => {
  if (days === null || days === undefined) return "-";

  const rounded = Number(days);
  const abs = Math.abs(rounded);
  return `${rounded} ${abs === 1 ? "day" : "days"}`;
};

const getDateMonthKey = (date) => {
  if (!date) return "";

  return getMonthKeyFromYearMonth(date.getFullYear(), date.getMonth());
};

const getDateQuarterKey = (date) => {
  if (!date) return "";

  const quarter = Math.floor(date.getMonth() / 3) + 1;
  return getQuarterKey(date.getFullYear(), quarter);
};

const getCompletedMonthKey = (item) => {
  return getDateMonthKey(getCompletedDate(item));
};

const getCompletedQuarterKey = (item) => {
  return getDateQuarterKey(getCompletedDate(item));
};

const getStartedMonthKey = (item) => {
  return getDateMonthKey(getStartedDate(item));
};

const getStartedQuarterKey = (item) => {
  return getDateQuarterKey(getStartedDate(item));
};

const getPercentageChange = (current, previous) => {
  if (!previous) return current ? 100 : 0;

  return Math.round(((current - previous) / previous) * 100);
};

const calculateSummaryTotals = ({
  cases = [],
  summaryMode = "month",
  selectedSummaryKey = "",
}) => {
  const isQuarter = summaryMode === "quarter";

  const previousKey = isQuarter
    ? getPreviousQuarterKey(selectedSummaryKey)
    : getPreviousMonthKey(selectedSummaryKey);

  const periodLabel = isQuarter
    ? getQuarterLabel(selectedSummaryKey)
    : getMonthLabel(selectedSummaryKey);

  const previousPeriodLabel = isQuarter ? "last quarter" : "last month";

  const totals = cases.reduce(
    (acc, item) => {
      const completedKey = isQuarter
        ? getCompletedQuarterKey(item)
        : getCompletedMonthKey(item);

      const startedKey = isQuarter
        ? getStartedQuarterKey(item)
        : getStartedMonthKey(item);

      const isCompleted = item.status === "completed";
      const isInProgress = item.status === "in_progress";
      const totalCost = isCompleted ? Number(item?.costs?.totalCost || 0) : 0;

      if (completedKey === selectedSummaryKey) {
        acc.periodTotal += totalCost;
        acc.periodCaseCount += 1;
        acc.periodCompletedCount += 1;
      }

      if (completedKey === previousKey) {
        acc.previousPeriodTotal += totalCost;
        acc.previousPeriodCaseCount += 1;
        acc.previousPeriodCompletedCount += 1;
      }

      if (isInProgress && startedKey === selectedSummaryKey) {
        acc.periodInProgressCount += 1;
      }

      if (isInProgress && startedKey === previousKey) {
        acc.previousPeriodInProgressCount += 1;
      }

      return acc;
    },
    {
      periodLabel,
      previousPeriodLabel,

      periodTotal: 0,
      previousPeriodTotal: 0,

      periodCaseCount: 0,
      previousPeriodCaseCount: 0,

      periodCompletedCount: 0,
      previousPeriodCompletedCount: 0,

      periodInProgressCount: 0,
      previousPeriodInProgressCount: 0,
    },
  );

  return {
    ...totals,

    periodChange: getPercentageChange(
      totals.periodTotal,
      totals.previousPeriodTotal,
    ),

    periodCaseCountChange: getPercentageChange(
      totals.periodCaseCount,
      totals.previousPeriodCaseCount,
    ),

    periodCompletedCountChange: getPercentageChange(
      totals.periodCompletedCount,
      totals.previousPeriodCompletedCount,
    ),

    periodInProgressCountChange: getPercentageChange(
      totals.periodInProgressCount,
      totals.previousPeriodInProgressCount,
    ),
  };
};

const getPageTitle = (catalogId) => {
  if (catalogId === "financial_planning") {
    return "Financial Planning: My Worksheet";
  }

  if (catalogId === "mortgage_protection") {
    return "Mortgage & Protection: My Worksheet";
  }

  return "Paraplanning: My Worksheet";
};

const getActiveFilterCount = (drawerFilters = {}) => {
  return Object.values(drawerFilters).reduce((total, value) => {
    if (!Array.isArray(value)) return total;
    return total + value.length;
  }, 0);
};

const getCaseTypeFilterId = (item) => {
  if (item.categoryId && item.itemId) {
    return `${item.categoryId}__${item.itemId}`;
  }

  return item.itemId || item.type || item.chargeSnapshot?.itemName || "";
};

const filterCasesWithDrawer = (cases = [], drawerFilters = {}) => {
  return cases.filter((item) => {
    const matchesCatalog =
      !drawerFilters.catalogIds.length ||
      drawerFilters.catalogIds.includes(item.catalogId);

    const adviserId = item.adviser?.email || "";
    const matchesAdviser =
      !drawerFilters.adviserIds.length ||
      drawerFilters.adviserIds.includes(adviserId);

    const caseTypeId = getCaseTypeFilterId(item);
    const matchesCaseType =
      !drawerFilters.caseTypeIds.length ||
      drawerFilters.caseTypeIds.includes(caseTypeId);

    const matchesStatus =
      !drawerFilters.statuses.length ||
      drawerFilters.statuses.includes(item.status);

    const assignedToId = item.assignedTo?.email || "";
    const matchesAssignedTo =
      !drawerFilters.assignedToIds.length ||
      drawerFilters.assignedToIds.includes(assignedToId);

    return (
      matchesCatalog &&
      matchesAdviser &&
      matchesCaseType &&
      matchesStatus &&
      matchesAssignedTo
    );
  });
};

const getSelectedOptions = (selectedIds = [], options = []) => {
  return selectedIds.map((id) => {
    const option = options.find((item) => item.id === id || item.value === id);

    return {
      id,
      label: option?.label || option?.name || id,
    };
  });
};

const normaliseEmail = (email = "") => {
  return String(email || "")
    .trim()
    .toLowerCase();
};

const hasHeadOfParaplanningPermission = (permissions = []) => {
  if (!Array.isArray(permissions)) return false;

  return permissions.some(
    (permission) =>
      String(permission || "")
        .trim()
        .toLowerCase() === "head of paraplanning",
  );
};

const getAssignedToOptionEmail = (email = "") => {
  const safeEmail = normaliseEmail(email);
  if (!safeEmail) return "";

  const matchedOption = PARAPLANNING_ASSIGNED_TO_OPTIONS.find(
    (option) => normaliseEmail(option.email) === safeEmail,
  );

  return matchedOption?.email || safeEmail;
};

const getBonusForSummaryMode = ({
  quarterlyBonusAmount = 0,
  summaryMode = "quarter",
}) => {
  const bonus = Number(quarterlyBonusAmount || 0);

  if (summaryMode === "month") {
    return bonus / 3;
  }

  return bonus;
};

const getSalaryDivisorForSummaryMode = (summaryMode = "quarter") => {
  return summaryMode === "month" ? 12 : 4;
};

const getBonusLabelForSummaryMode = (summaryMode = "quarter") => {
  return summaryMode === "month" ? "Monthly Bonus" : "Quarterly Bonus";
};

const getBonusMathText = ({
  summaryMode = "quarter",
  chargeTotal = 0,
  annualSalary = 0,
  salaryForPeriod = 0,
  chargeVsSalaryPercentage = 0,
  quarterlyBonusAmount = 0,
  bonusAmount = 0,
}) => {
  const divisor = getSalaryDivisorForSummaryMode(summaryMode);
  const modeLabel = summaryMode === "month" ? "Monthly" : "Quarterly";

  if (!annualSalary || !salaryForPeriod) {
    return "Upload salary first";
  }

  const salaryMath = `${formatParaplanningCurrency(
    annualSalary,
  )} ÷ ${divisor} = ${formatParaplanningCurrency(salaryForPeriod)}`;

  const chargeMath = `${formatParaplanningCurrency(
    chargeTotal,
  )} ÷ ${formatParaplanningCurrency(
    salaryForPeriod,
  )} = ${formatParaplanningPercent(chargeVsSalaryPercentage)}`;

  if (summaryMode === "month") {
    return `${modeLabel} maths: Salary ${salaryMath}. Charge vs salary ${chargeMath}. Bonus estimate ${formatParaplanningCurrency(
      quarterlyBonusAmount,
    )} ÷ 3 = ${formatParaplanningCurrency(bonusAmount)}.`;
  }

  return `${modeLabel} maths: Salary ${salaryMath}. Charge vs salary ${chargeMath}. Bonus band ${formatParaplanningCurrency(
    quarterlyBonusAmount,
  )}.`;
};

const getPeriodCompletedCases = ({
  cases = [],
  summaryMode = "quarter",
  selectedSummaryKey = "",
}) => {
  const isQuarter = summaryMode === "quarter";

  return cases.filter((item) => {
    const completedKey = isQuarter
      ? getCompletedQuarterKey(item)
      : getCompletedMonthKey(item);

    return completedKey === selectedSummaryKey;
  });
};

const getAssignedToEmail = (item = {}) => {
  return normaliseEmail(
    item?.assignedTo?.email ||
      item?.assignedToEmail ||
      item?.assignedTo?.value ||
      "",
  );
};

const getEmailLabel = (email = "") => {
  const clean = String(email || "").trim();
  if (!clean.includes("@")) return clean || "Unknown User";

  const [local] = clean.split("@");

  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const buildUserPermissionRecords = (docs = []) => {
  return docs
    .map((docSnap) => {
      const data = docSnap.data() || {};
      const email = normaliseEmail(data.email || docSnap.id || "");
      const permissions = Array.isArray(data.permissions)
        ? data.permissions
        : [];

      return {
        id: email || docSnap.id,
        email,
        label: getEmailLabel(email),
        permissions,
      };
    })
    .filter((item) => item.email)
    .sort((a, b) => a.email.localeCompare(b.email));
};

const ExcelMonthPopup = ({
  open,
  monthOptions = [],
  loading = false,
  onClose,
  onDownload,
}) => {
  if (!open) return null;

  return (
    <div className="ParaplanningExcelPopupOverlay">
      <div className="ParaplanningExcelPopup">
        <div className="ParaplanningExcelPopup__Header">
          <div>
            <div className="FP_Subtitle_Semibold primary">
              Download Accounts Excel
            </div>

            <div className="FP_Help_Text">
              Select the completed month you want to export.
            </div>
          </div>

          <button
            type="button"
            className="ParaplanningExcelPopup__Close"
            onClick={onClose}
            aria-label="Close Excel export popup"
          >
            <Close />
          </button>
        </div>

        <div className="ParaplanningExcelPopup__Months">
          {monthOptions.map((option) => (
            <button
              key={option.id}
              type="button"
              className="ParaplanningExcelPopup__MonthButton FP_Content"
              disabled={loading}
              onClick={() => onDownload(option.monthKey)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// const getBonusForSummaryMode = ({
//   quarterlyBonusAmount = 0,
//   summaryMode = "quarter",
// }) => {
//   const bonus = Number(quarterlyBonusAmount || 0);

//   return summaryMode === "month" ? bonus / 3 : bonus;
// };

// const getSalaryComparisonText = (chargeVsSalaryPercentage = 0) => {
//   const percentage = Number(chargeVsSalaryPercentage || 0);

//   if (!percentage) return "No charge vs salary yet";

//   const differenceFromSalary = percentage - 100;

//   if (differenceFromSalary > 0) {
//     return `${differenceFromSalary.toFixed(1)}% above salary`;
//   }

//   if (differenceFromSalary < 0) {
//     return `${Math.abs(differenceFromSalary).toFixed(1)}% below salary`;
//   }

//   return "Equal to salary";
// };

const getSalaryComparisonInfo = (chargeVsSalaryPercentage = 0) => {
  const percentage = Number(chargeVsSalaryPercentage || 0);

  if (!percentage) {
    return {
      text: "No charge vs salary yet",
      changeValue: null,
      direction: "none",
      variant: "purple",
    };
  }

  const differenceFromSalary = percentage - 100;
  const roundedDifference = Number(differenceFromSalary.toFixed(1));

  if (roundedDifference > 0) {
    return {
      text: `${roundedDifference.toFixed(1)}% above salary`,
      changeValue: roundedDifference,
      direction: "up",
      variant: "purple",
    };
  }

  if (roundedDifference < 0) {
    return {
      text: `${Math.abs(roundedDifference).toFixed(1)}% below salary`,
      changeValue: roundedDifference,
      direction: "down",
      variant: "red",
    };
  }

  return {
    text: "Equal to salary",
    changeValue: null,
    direction: "none",
    variant: "purple",
  };
};

const logParaplannerBonusMath = ({
  name = "",
  email = "",
  summaryMode = "quarter",
  periodKey = "",
  annualSalary = 0,
  salaryForPeriod = 0,
  chargeTotal = 0,
  chargeVsSalaryPercentage = 0,
  quarterlyBonusAmount = 0,
  displayedBonusAmount = 0,
  bonusBlockedByGrade = false,
}) => {
  const divisor = summaryMode === "month" ? 12 : 4;

  // console.log("[Paraplanning Bonus Maths]", {
  //   paraplanner: name,
  //   email,
  //   summaryMode,
  //   periodKey,

  //   annualSalary,
  //   salaryDivisor: divisor,
  //   salaryForPeriodCalculation: `${annualSalary} / ${divisor}`,
  //   salaryForPeriod,

  //   chargeTotal,
  //   chargeVsSalaryCalculation: `${chargeTotal} / ${salaryForPeriod} * 100`,
  //   chargeVsSalaryPercentage,
  //   percentageAgainstSalary: getSalaryComparisonInfo(chargeVsSalaryPercentage)
  //     .text,

  //   // percentageAgainstSalary: getSalaryComparisonText(chargeVsSalaryPercentage),

  //   quarterlyBonusAmount,
  //   displayedBonusAmount,
  //   displayedBonusCalculation:
  //     summaryMode === "month"
  //       ? `${quarterlyBonusAmount} / 3`
  //       : `${quarterlyBonusAmount}`,

  //   bonusBlockedByGrade,
  // });
};

const ParaplanningDashboard = () => {
  const [searchParams] = useSearchParams();
  const initialExpandedCaseId = searchParams.get("caseId") || "";
  const { userPermissions = [] } = useUserPermissions();

  // console.log("User Permissions:", userPermissions);

  const loggedInEmail =
    auth.currentUser?.email || localStorage.getItem("userEmail") || "";
  const normalisedLoggedInEmail = normaliseEmail(loggedInEmail);

  const loggedInPermissionState =
    getParaplanningPermissionState(userPermissions);
  const isLoggedInAdmin = loggedInPermissionState.isAdmin;
  const isHeadOfParaplanning = hasHeadOfParaplanningPermission(userPermissions);

  const [staffPermissionUsers, setStaffPermissionUsers] = useState([]);
  const [loadingStaffPermissionUsers, setLoadingStaffPermissionUsers] =
    useState(false);
  const [selectedUserEmail, setSelectedUserEmail] = useState("");

  useEffect(() => {
    let alive = true;

    if (!isLoggedInAdmin) {
      setStaffPermissionUsers([]);
      setSelectedUserEmail("");
      return undefined;
    }

    const loadStaffUsers = async () => {
      setLoadingStaffPermissionUsers(true);

      try {
        const staffRef = collection(db, "Authorized Members", "Staff", "Data");
        const snapshot = await getDocs(staffRef);

        if (!alive) return;

        const users = buildUserPermissionRecords(snapshot.docs || []);

        const hasLoggedInUser = users.some(
          (item) => item.email === normalisedLoggedInEmail,
        );

        const mergedUsers = hasLoggedInUser
          ? users
          : [
              {
                id: normalisedLoggedInEmail || "current-user",
                email: normalisedLoggedInEmail,
                label: getEmailLabel(normalisedLoggedInEmail),
                permissions: userPermissions,
              },
              ...users,
            ].filter((item) => item.email);

        setStaffPermissionUsers(mergedUsers);

        setSelectedUserEmail((prev) => {
          if (prev && mergedUsers.some((item) => item.email === prev)) {
            return prev;
          }

          if (
            normalisedLoggedInEmail &&
            mergedUsers.some((item) => item.email === normalisedLoggedInEmail)
          ) {
            return normalisedLoggedInEmail;
          }

          return mergedUsers[0]?.email || "";
        });
      } catch {
        if (!alive) return;
        setStaffPermissionUsers([]);
      } finally {
        if (alive) {
          setLoadingStaffPermissionUsers(false);
        }
      }
    };

    loadStaffUsers();

    return () => {
      alive = false;
    };
  }, [isLoggedInAdmin, normalisedLoggedInEmail, userPermissions]);

  const selectedUserRecord = useMemo(() => {
    if (!isLoggedInAdmin) return null;

    return (
      staffPermissionUsers.find((item) => item.email === selectedUserEmail) ||
      null
    );
  }, [staffPermissionUsers, selectedUserEmail, isLoggedInAdmin]);

  const effectiveUserEmail =
    selectedUserRecord?.email || normalisedLoggedInEmail || loggedInEmail;

  const effectiveUserPermissions =
    selectedUserRecord?.permissions || userPermissions;

  const isEffectiveHeadOfParaplanning = hasHeadOfParaplanningPermission(
    effectiveUserPermissions,
  );

  const effectivePermissionState = getParaplanningPermissionState(
    effectiveUserPermissions,
  );

  const loggedInParaplannerName = getParaplannerNameByEmail(effectiveUserEmail);
  const {
    isAdmin,
    isAccountsAdmin,
    isParaplanner,
    canView,
    canManageAllCases,
  } = effectivePermissionState;
  const { salaryMap } = useParaplannerSalaries(isAdmin);
  const defaultCatalogId = getParaplannerTypeByEmail(effectiveUserEmail);

  const [filters, setFilters] = useState({
    catalogId: "",
    search: "",
    status: "all",
    startDate: "",
    monthKey: "",
    quarterKey: "",
    showArchived: false,
    userEmail: effectiveUserEmail,
    isAdmin,
    canView,
    canManageAllCases,
    assignedTo: isHeadOfParaplanning
      ? getAssignedToOptionEmail(normalisedLoggedInEmail)
      : "",
  });

  useEffect(() => {
    if (!isEffectiveHeadOfParaplanning) return;

    const defaultAssignedTo = getAssignedToOptionEmail(effectiveUserEmail);
    if (!defaultAssignedTo) return;

    setFilters((prev) => {
      if (prev.assignedTo) return prev;

      return {
        ...prev,
        assignedTo: defaultAssignedTo,
      };
    });
  }, [isEffectiveHeadOfParaplanning, effectiveUserEmail]);

  const [mainSummaryMode, setMainSummaryMode] = useState("quarter");
  const [mainSelectedSummaryKey, setMainSelectedSummaryKey] = useState(
    getCurrentQuarterKey(),
  );

  const [staffSummaryMode, setStaffSummaryMode] = useState("month");
  const [staffSelectedSummaryKey, setStaffSelectedSummaryKey] =
    useState(getCurrentMonthKey());

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerFilters, setDrawerFilters] = useState(EMPTY_DRAWER_FILTERS);

  const [showCaseModal, setShowCaseModal] = useState(false);
  const [editingCase, setEditingCase] = useState(null);
  const [excelPopupOpen, setExcelPopupOpen] = useState(false);
  const [activeCaseTab, setActiveCaseTab] = useState("assigned");

  const { advisers } = useAdviserListWithUnappointed();

  const { catalogs: financialPlanningCatalogs } =
    useParaplanningCharges("financial_planning");

  const { catalogs: mortgageProtectionCatalogs } = useParaplanningCharges(
    "mortgage_protection",
  );

  const adviserOptions = useMemo(() => {
    return buildAdviserOptions(advisers);
  }, [advisers]);

  const assignedToOptions = useMemo(() => {
    return buildAssignedToOptions(PARAPLANNING_ASSIGNED_TO_OPTIONS);
  }, []);

  const caseTypeOptions = useMemo(() => {
    const financialPlanningCatalog = financialPlanningCatalogs?.[0] || null;
    const mortgageProtectionCatalog = mortgageProtectionCatalogs?.[0] || null;

    const financialPlanningOptions = buildCaseTypeOptions(
      financialPlanningCatalog,
    ).map((option) => ({
      ...option,
      id: option.value,
      catalogId: "financial_planning",
    }));

    const mortgageProtectionOptions = buildCaseTypeOptions(
      mortgageProtectionCatalog,
    ).map((option) => ({
      ...option,
      id: option.value,
      catalogId: "mortgage_protection",
    }));

    return [...financialPlanningOptions, ...mortgageProtectionOptions];
  }, [financialPlanningCatalogs, mortgageProtectionCatalogs]);

  const monthSummaryOptions = useMemo(() => {
    return getMonthOptionsForYear();
  }, []);

  const quarterSummaryOptions = useMemo(() => {
    return getQuarterOptionsForYear();
  }, []);

  const mainSummaryOptions = useMemo(() => {
    return mainSummaryMode === "quarter"
      ? quarterSummaryOptions
      : monthSummaryOptions;
  }, [mainSummaryMode, monthSummaryOptions, quarterSummaryOptions]);

  const staffSummaryOptions = useMemo(() => {
    return staffSummaryMode === "quarter"
      ? quarterSummaryOptions
      : monthSummaryOptions;
  }, [staffSummaryMode, monthSummaryOptions, quarterSummaryOptions]);

  // Admins get combined summary stats; non-admin users get personal stats only.
  const summaryCanManageAllCases = isAdmin;

  const excelMonthOptions = useMemo(() => {
    return getExcelMonthOptionsFromJan();
  }, []);

  /**
   * This query is for the table.
   * It follows the normal top filters.
   */
  const tableQueryFilters = useMemo(
    () => ({
      ...filters,
      userEmail: effectiveUserEmail,
      userName: loggedInParaplannerName,
      isAdmin,
      canManageAllCases,
    }),
    [
      filters,
      effectiveUserEmail,
      loggedInParaplannerName,
      isAdmin,
      canManageAllCases,
    ],
  );

  /**
   * This query is for the summary cards.
   * It deliberately ignores catalog/search/status/month/quarter/assignedTo filters.
   * This keeps the summary fixed even when the table is filtered.
   */
  const summaryQueryFilters = useMemo(
    () => ({
      catalogId: "",
      search: "",
      status: "all",
      startDate: "",
      monthKey: "",
      quarterKey: "",
      showArchived: filters.showArchived,
      userEmail: effectiveUserEmail,
      userName: loggedInParaplannerName,
      isAdmin,
      canManageAllCases: summaryCanManageAllCases,
      assignedTo: "",
    }),
    [
      filters.showArchived,
      effectiveUserEmail,
      loggedInParaplannerName,
      isAdmin,
      summaryCanManageAllCases,
    ],
  );

  const exportQueryFilters = useMemo(
    () => ({
      catalogId: "",
      search: "",
      status: "all",
      startDate: "",
      monthKey: "",
      quarterKey: "",
      showArchived: true,
      userEmail: "",
      userName: "",
      isAdmin: true,
      canManageAllCases: true,
      assignedTo: "",
    }),
    [],
  );

  const { cases, loadingCases, casesError, refreshCases } =
    useParaplanningCases(tableQueryFilters);

  const { cases: summaryCases, refreshCases: refreshSummaryCases } =
    useParaplanningCases(summaryQueryFilters);

  const { cases: exportCases } = useParaplanningCases(exportQueryFilters);

  const filteredCases = useMemo(() => {
    return filterCasesWithDrawer(cases, drawerFilters);
  }, [cases, drawerFilters]);
  const assignedCases = useMemo(() => {
    return filteredCases.filter((item) => hasAssignedTo(item));
  }, [filteredCases]);

  const unassignedCases = useMemo(() => {
    return filteredCases.filter((item) => !hasAssignedTo(item));
  }, [filteredCases]);

  const caseTabs = useMemo(
    () => [
      {
        id: "assigned",
        label: "Assigned Cases",
        count: assignedCases.length,
      },
      {
        id: "unassigned",
        label: "Cases in Pipeline",
        count: unassignedCases.length,
      },
    ],
    [assignedCases.length, unassignedCases.length],
  );

  const casesForActiveTab =
    activeCaseTab === "unassigned" ? unassignedCases : assignedCases;

  const averageTurnaroundDaysForDashboard = useMemo(() => {
    return getAverageTurnaroundDays(casesForActiveTab);
  }, [casesForActiveTab]);

  const summaryStatsCases = useMemo(() => {
    if (isAdmin) return summaryCases;

    if (!isParaplanner) return [];

    const loggedInSafeEmail = normaliseEmail(effectiveUserEmail);

    return summaryCases.filter((item) => {
      return getAssignedToEmail(item) === loggedInSafeEmail;
    });
  }, [summaryCases, isAdmin, isParaplanner, effectiveUserEmail]);

  const totals = useMemo(() => {
    return calculateSummaryTotals({
      cases: summaryStatsCases,
      summaryMode: mainSummaryMode,
      selectedSummaryKey: mainSelectedSummaryKey,
    });
  }, [summaryStatsCases, mainSummaryMode, mainSelectedSummaryKey]);

  const paraplannerIndividualStats = useMemo(() => {
    if (!isAdmin) return [];

    return PARAPLANNING_ASSIGNED_TO_OPTIONS.map((person) => {
      const personEmail = normaliseEmail(person.email);

      const personCases = summaryCases.filter((item) => {
        return getAssignedToEmail(item) === personEmail;
      });

      const totalsForPerson = calculateSummaryTotals({
        cases: personCases,
        summaryMode: staffSummaryMode,
        selectedSummaryKey: staffSelectedSummaryKey,
      });

      const completedCasesForPeriod = getPeriodCompletedCases({
        cases: personCases,
        summaryMode: staffSummaryMode,
        selectedSummaryKey: staffSelectedSummaryKey,
      });

      const salaryRecord = salaryMap[personEmail] || {};

      const annualSalary = getAnnualSalaryForPeriod({
        salaryRecord,
        selectedPeriodKey: staffSelectedSummaryKey,
        summaryMode: staffSummaryMode,
      });

      const salaryForPeriod = getSalaryForSummaryMode(
        annualSalary,
        staffSummaryMode,
      );

      const chargeVsSalaryPercentage = getChargeVsSalaryPercentage({
        chargeTotal: totalsForPerson.periodTotal,
        salaryForPeriod,
      });

      const bonusBlockedByGrade = false;
      // const bonusBlockedByGrade = hasBonusBlockingGrade(completedCasesForPeriod);

      // const quarterlyBonusAmount = !bonusBlockedByGrade
      //   ? getParaplanningBonusAmount(chargeVsSalaryPercentage)
      //   : 0;

      const quarterlyBonusAmount = !bonusBlockedByGrade
        ? getParaplanningBonusAmount(chargeVsSalaryPercentage)
        : 0;

      const bonusAmount = !bonusBlockedByGrade
        ? getBonusForSummaryMode({
            quarterlyBonusAmount,
            summaryMode: staffSummaryMode,
          })
        : 0;

      const salaryComparisonInfo = salaryForPeriod
        ? getSalaryComparisonInfo(chargeVsSalaryPercentage)
        : { text: "Upload salary first", changeValue: 0, variant: "default" };

      logParaplannerBonusMath({
        name: person.label || person.name || person.email,
        email: person.email,
        summaryMode: staffSummaryMode,
        periodKey: staffSelectedSummaryKey,
        annualSalary,
        salaryForPeriod,
        chargeTotal: totalsForPerson.periodTotal,
        chargeVsSalaryPercentage,
        quarterlyBonusAmount,
        displayedBonusAmount: bonusAmount,
        bonusBlockedByGrade,
      });

      const bonusMathText = bonusBlockedByGrade
        ? "Bonus blocked because a Grade 3/4 case was found in this period."
        : getBonusMathText({
            summaryMode: staffSummaryMode,
            chargeTotal: totalsForPerson.periodTotal,
            annualSalary,
            salaryForPeriod,
            chargeVsSalaryPercentage,
            quarterlyBonusAmount,
            bonusAmount,
          });

      return {
        email: person.email,
        label: person.label || person.name || person.email,
        totals: totalsForPerson,
        annualSalary,
        salaryForPeriod,
        chargeVsSalaryPercentage,
        quarterlyBonusAmount,
        bonusAmount,
        salaryComparisonText: salaryComparisonInfo.text,
        salaryComparisonChangeValue: salaryComparisonInfo.changeValue,
        bonusCardVariant: bonusBlockedByGrade
          ? "red"
          : salaryComparisonInfo.variant,
        bonusBlockedByGrade,
        completedCasesForPeriod,
      };
    }).filter((person) => person.email);
  }, [
    summaryCases,
    salaryMap,
    isAdmin,
    staffSummaryMode,
    staffSelectedSummaryKey,
  ]);

  const activeFilterCount = useMemo(() => {
    return getActiveFilterCount(drawerFilters);
  }, [drawerFilters]);

  const pageTitle = getPageTitle(filters.catalogId);

  const selectedCatalogs = useMemo(() => {
    return drawerFilters.catalogIds.map((id) => ({
      id,
      label: CATALOG_LABELS[id] || id,
    }));
  }, [drawerFilters.catalogIds]);

  const selectedAdvisers = useMemo(() => {
    return getSelectedOptions(drawerFilters.adviserIds, adviserOptions);
  }, [drawerFilters.adviserIds, adviserOptions]);

  const selectedCaseTypes = useMemo(() => {
    return getSelectedOptions(drawerFilters.caseTypeIds, caseTypeOptions);
  }, [drawerFilters.caseTypeIds, caseTypeOptions]);

  const selectedStatuses = useMemo(() => {
    return drawerFilters.statuses.map((id) => ({
      id,
      label: STATUS_LABELS[id] || id,
    }));
  }, [drawerFilters.statuses]);

  const selectedAssignedTo = useMemo(() => {
    return getSelectedOptions(drawerFilters.assignedToIds, assignedToOptions);
  }, [drawerFilters.assignedToIds, assignedToOptions]);

  const handleMainSummaryModeChange = (mode) => {
    setMainSummaryMode(mode);

    if (mode === "quarter") {
      setMainSelectedSummaryKey(getCurrentQuarterKey());
      return;
    }

    setMainSelectedSummaryKey(getCurrentMonthKey());
  };

  const handleStaffSummaryModeChange = (mode) => {
    setStaffSummaryMode(mode);

    if (mode === "quarter") {
      setStaffSelectedSummaryKey(getCurrentQuarterKey());
      return;
    }

    setStaffSelectedSummaryKey(getCurrentMonthKey());
  };

  const exportCasesForMonth = (monthKey) => {
    exportParaplanningCasesToExcel({
      cases: exportCases,
      monthKey,
      filePrefix: "Paraplanning Worksheet",
    });

    setExcelPopupOpen(false);
  };

  const exportAllMonths = () => {
    exportAllParaplanningMonthsToExcel({
      cases: exportCases,
      filePrefix: "Paraplanning Worksheet",
    });
  };

  const removeDrawerFilter = (field, id) => {
    setDrawerFilters((prev) => ({
      ...prev,
      [field]: (prev[field] || []).filter((value) => value !== id),
    }));
  };

  const clearDrawerFilters = () => {
    setDrawerFilters(EMPTY_DRAWER_FILTERS);
  };

  const openAddCaseModal = () => {
    setEditingCase(null);
    setShowCaseModal(true);
  };

  const openEditCaseModal = (caseItem) => {
    setEditingCase(caseItem);
    setShowCaseModal(true);
  };

  const closeCaseModal = () => {
    setEditingCase(null);
    setShowCaseModal(false);
  };

  const handleModalSaved = () => {
    closeCaseModal();
    refreshCases?.();
    refreshSummaryCases?.();
  };

  return (
    <div className="main-content">
      <Helmet>
        <title>Paraplanning Worksheet</title>
      </Helmet>

      <Sidebar />

      <div className="Main-Container ParaplanningDashboard">
        <Breadcrumb
          items={[
            {
              label: "Paraplanning",
              to: "/Department/Paraplanning",
            },
            { label: "My Worksheet" },
          ]}
        />
        <div className="ParaplanningDashboard__Header">
          <div className="ParaplanningDashboard__HeaderLeft">
            <div className="ParaplanningDashboard__TitleRow">
              <h3 className="FP_H3">{pageTitle}</h3>
            </div>

            <div className="ParaplanningDashboard__ScopeText">
              {isAdmin
                ? "Admin view: showing all cases"
                : canManageAllCases
                  ? "Showing all cases"
                  : `Showing cases assigned to ${
                      loggedInParaplannerName || effectiveUserEmail
                    }`}
              {isAdmin && (
                <div className="FP_Help_Text">
                  Average Turnaround Time:{" "}
                  {formatTurnaroundDays(averageTurnaroundDaysForDashboard)}
                </div>
              )}
            </div>
          </div>

          <div className="ParaplanningDashboard__HeaderActions">
            {isAccountsAdmin && (
              <button
                type="button"
                className="ParaplanningDashboard__ExcelButton"
                onClick={() => setExcelPopupOpen(true)}
                disabled={!filteredCases.length}
                title="Download accounts Excel"
              >
                Excel
              </button>
            )}

            {/* Uncomment this if you want the full-year/all-months export button. */}
            {isAccountsAdmin && (
              <button
                type="button"
                className="ParaplanningDashboard__ExcelButton"
                onClick={exportAllMonths}
                disabled={!summaryCases.length}
                title="Download all months in one Excel file"
              >
                2026
              </button>
            )}

            <button
              type="button"
              className="ParaplanningDashboard__ActionButton"
              onClick={() => setDrawerOpen(true)}
            >
              <FilterIcon />
              <span>Filters</span>

              {activeFilterCount > 0 && (
                <span className="ParaplanningDashboard__FilterCount">
                  {activeFilterCount}
                </span>
              )}
            </button>

            {(isParaplanner || isAdmin) && (
              <SecondaryButton
                type="button"
                onClick={openAddCaseModal}
                label="Add New Case"
              />
            )}
          </div>
        </div>

        {/* {isLoggedInAdmin && (
          <section className="ParaplanningUserAccessPreview">
            <div className="ParaplanningUserAccessPreview__Header">
              <div className="FP_Subtitle_Semibold primary">View As User</div>
              <div className="FP_Help_Text">
                Select a staff user to preview permissions and paraplanning
                view.
              </div>
            </div>

            {loadingStaffPermissionUsers ? (
              <div className="FP_Help_Text">
                Loading users and permissions...
              </div>
            ) : !staffPermissionUsers.length ? (
              <div className="FP_Help_Text">No staff users found.</div>
            ) : (
              <div className="ParaplanningUserAccessPreview__Grid">
                {staffPermissionUsers.map((user) => {
                  const checked = user.email === selectedUserEmail;

                  return (
                    <label
                      key={user.email}
                      className={`ParaplanningUserAccessPreview__Card ${
                        checked
                          ? "ParaplanningUserAccessPreview__Card--Active"
                          : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="paraplanning-user-preview"
                        value={user.email}
                        checked={checked}
                        onChange={() => setSelectedUserEmail(user.email)}
                      />

                      <div className="ParaplanningUserAccessPreview__CardBody">
                        <div className="FP_Content_Semibold">{user.label}</div>
                        <div className="FP_Help_Text">{user.email}</div>

                        <div className="ParaplanningUserAccessPreview__PermissionRow">
                          {user.permissions.length
                            ? user.permissions.map((permission) => (
                                <span
                                  key={`${user.email}-${permission}`}
                                  className="ParaplanningUserAccessPreview__PermissionChip"
                                >
                                  {permission}
                                </span>
                              ))
                            : "No permissions"}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </section>
        )} */}

        {/* {isAdmin && (
          <ParaplanningSummaryCards
            totals={totals}
            summaryMode={summaryMode}
            summaryOptions={summaryOptions}
            selectedSummaryKey={selectedSummaryKey}
            onSummaryModeChange={handleSummaryModeChange}
            onSummaryKeyChange={setSelectedSummaryKey}
          />
        )} */}

        {(isAdmin || isParaplanner) && (
          <section className="ParaplanningCombinedStats">
            <div className="ParaplanningCombinedStats__Header">
              <div className="FP_Subtitle_Semibold primary">
                {isAdmin ? "" : "My Summary"}
              </div>
            </div>
            <ParaplanningSummaryCards
              totals={totals}
              summaryMode={mainSummaryMode}
              summaryOptions={mainSummaryOptions}
              selectedSummaryKey={mainSelectedSummaryKey}
              onSummaryModeChange={handleMainSummaryModeChange}
              onSummaryKeyChange={setMainSelectedSummaryKey}
            />
          </section>
        )}

        {isAdmin && (
          <section className="ParaplanningIndividualStats">
            <div className="ParaplanningIndividualStats__Header">
              <div>
                <div className="ParaplanningIndividualStats__Title FP_Subtitle_Semibold primary">
                  Individual Staff Summary
                </div>

                <div className="ParaplanningIndividualStats__Subtitle FP_Help_Text">
                  Individual stats for each paraplanner.
                </div>
              </div>

              <div className="ParaplanningIndividualStats__Controls">
                <ParaplanningSummaryCards
                  totals={{}}
                  summaryMode={staffSummaryMode}
                  summaryOptions={staffSummaryOptions}
                  selectedSummaryKey={staffSelectedSummaryKey}
                  onSummaryModeChange={handleStaffSummaryModeChange}
                  onSummaryKeyChange={setStaffSelectedSummaryKey}
                  hideCards
                />
              </div>
            </div>

            <div className="ParaplanningIndividualStats__Horizontal">
              {paraplannerIndividualStats.map((person) => (
                <div
                  key={person.email}
                  className="ParaplanningIndividualStats__Card"
                >
                  <div className="ParaplanningIndividualStats__StaffHeader">
                    <div className="ParaplanningIndividualStats__Name FP_Content_Semibold">
                      {person.label}
                    </div>

                    <div className="ParaplanningIndividualStats__Email FP_Help_Text">
                      {person.email}
                    </div>
                  </div>

                  <ParaplanningSummaryCards
                    totals={person.totals}
                    summaryMode={staffSummaryMode}
                    summaryOptions={staffSummaryOptions}
                    selectedSummaryKey={staffSelectedSummaryKey}
                    onSummaryModeChange={handleStaffSummaryModeChange}
                    onSummaryKeyChange={setStaffSelectedSummaryKey}
                    hideFilters
                    extraCards={
                      isAdmin
                        ? [
                            {
                              id: `${person.email}-bonus`,
                              label:
                                getBonusLabelForSummaryMode(staffSummaryMode),
                              value: person.bonusBlockedByGrade
                                ? "Blocked"
                                : person.salaryForPeriod
                                  ? formatParaplanningCurrency(
                                      person.bonusAmount,
                                    )
                                  : "No salary",
                              helperText: person.salaryComparisonText,
                              changeValue: person.salaryComparisonChangeValue,
                              variant: person.bonusCardVariant,
                            },
                          ]
                        : []
                    }
                  />
                </div>
              ))}
            </div>
          </section>
        )}
        {/* <ParaplanningExcelImport loggedInEmail={loggedInEmail} /> */}

        <ParaplanningFilters filters={filters} setFilters={setFilters} />

        <ParaplanningSelectedFilterChips
          selectedCatalogs={selectedCatalogs}
          selectedAdvisers={selectedAdvisers}
          selectedCaseTypes={selectedCaseTypes}
          selectedStatuses={selectedStatuses}
          selectedAssignedTo={selectedAssignedTo}
          onRemoveCatalog={(id) => removeDrawerFilter("catalogIds", id)}
          onRemoveAdviser={(id) => removeDrawerFilter("adviserIds", id)}
          onRemoveCaseType={(id) => removeDrawerFilter("caseTypeIds", id)}
          onRemoveStatus={(id) => removeDrawerFilter("statuses", id)}
          onRemoveAssignedTo={(id) => removeDrawerFilter("assignedToIds", id)}
          onClearAll={clearDrawerFilters}
        />

        {/* {casesError && (
          <div className="ParaplanningDashboard__Error">{casesError}</div>
        )} */}

        {/* <ParaplanningCasesTable
          cases={filteredCases}
          loading={loadingCases}
          onRefresh={refreshCases}
          onEditCase={openEditCaseModal}
          loggedInEmail={loggedInEmail}
          loggedInParaplannerName={loggedInParaplannerName}
        /> */}
        <div className="ParaplanningDashboard__CaseTabs">
          <Tabs
            tabs={caseTabs}
            activeTab={activeCaseTab}
            onTabChange={setActiveCaseTab}
          />
        </div>

        <ParaplanningCasesTable
          cases={casesForActiveTab}
          loading={loadingCases}
          onRefresh={refreshCases}
          onEditCase={openEditCaseModal}
          loggedInEmail={effectiveUserEmail}
          loggedInParaplannerName={loggedInParaplannerName}
          tableMode={activeCaseTab}
          emptyMessage={
            activeCaseTab === "unassigned"
              ? "No unassigned cases found."
              : "No assigned cases found."
          }
          initialExpandedCaseId={initialExpandedCaseId}
        />

        <ParaplanningFiltersDrawer
          open={drawerOpen}
          filters={drawerFilters}
          adviserOptions={adviserOptions}
          caseTypeOptions={caseTypeOptions}
          assignedToOptions={assignedToOptions}
          onClose={() => setDrawerOpen(false)}
          onApply={setDrawerFilters}
          onReset={clearDrawerFilters}
        />

        {showCaseModal && (
          <AddCaseModal
            defaultCatalogId={filters.catalogId || defaultCatalogId}
            loggedInEmail={effectiveUserEmail}
            initialCase={editingCase}
            mode={editingCase ? "edit" : "create"}
            onClose={closeCaseModal}
            onCreated={handleModalSaved}
          />
        )}

        <ExcelMonthPopup
          open={excelPopupOpen}
          monthOptions={excelMonthOptions}
          onClose={() => setExcelPopupOpen(false)}
          onDownload={exportCasesForMonth}
        />
      </div>
    </div>
  );
};

export default ParaplanningDashboard;
